/**
 * Builds Firebase Storage object paths for the broker / user / client / deal layout.
 *
 * Every file that holds broker, client or user data lives under one owner folder, keyed by
 * the owner's Firestore document id, so storage.rules can isolate access at that level.
 * See docs/hubspot-migration/STORAGE_MIGRATION_STATUS.md for the layout and the access rule
 * of each folder.
 *
 * Mirrored in backend/functions/helper/storagePaths.js. Change both in the same change.
 */

export const STORAGE_PURPOSES = {
	logo: 'logo',
	soaBackground: 'soaBackground',
	soaHeader: 'soaHeader',
	emailTemplates: 'emailTemplates',
	signatures: 'signatures',
	invoices: 'invoices',
	profile: 'profile',
	supportTickets: 'supportTickets',
	editorFiles: 'editorFiles',
	emailDrafts: 'emailDrafts',
	attachments: 'attachments',
	riskInfo: 'riskInfo',
	quotes: 'quotes',
	portfolioStatements: 'portfolioStatements',
	workspaces: 'workspaces',
	generatedSOA: 'generatedSOA',
	debtorReminders: 'debtorReminders',
	engageReports: 'engageReports',
};

const P = STORAGE_PURPOSES;

// Purposes each owner folder may hold.
const OWNER_PURPOSES = {
	brokerData: [P.logo, P.soaBackground, P.soaHeader, P.emailTemplates, P.signatures, P.invoices],
	userData: [P.profile, P.supportTickets, P.editorFiles, P.emailDrafts],
	clientData: [P.attachments, P.workspaces, P.emailDrafts],
	relationshipCompanyData: [P.attachments, P.workspaces],
	deals: [
		P.attachments,
		P.riskInfo,
		P.quotes,
		P.portfolioStatements,
		P.workspaces,
		P.generatedSOA,
		P.debtorReminders,
		P.engageReports,
		P.emailDrafts,
		P.invoices,
	],
};

// Shared template roots, keyed by the template document id.
const TEMPLATE_ROOTS = ['unlayerEmailTemplates', 'aiHtmlTemplates'];

const assertSegment = (value, label) => {
	if (typeof value !== 'string' || value === '' || value.includes('/')) {
		throw new Error(`Invalid storage path ${label}: ${value}`);
	}
};

const assertPurpose = (owner, purpose) => {
	if (!OWNER_PURPOSES[owner].includes(purpose)) {
		throw new Error(`Storage purpose ${purpose} is not allowed under ${owner}`);
	}
};

/** Makes free text (a file or signature name) safe to use as one path segment. */
export const encodeSegment = (name) => name.replaceAll('/', '-');

const joinPath = (...segments) => {
	segments.forEach((segment, index) => assertSegment(segment, `segment ${index}`));
	return segments.join('/');
};

/** brokerData/{brokerGroupId}/{purpose}/… */
export const brokerStoragePath = (brokerGroupId, purpose, ...segments) => {
	assertPurpose('brokerData', purpose);
	return joinPath('brokerData', brokerGroupId, purpose, ...segments);
};

/** brokerData/{brokerGroupId}/signatures/{userDocId}/{name}/signature.html */
export const brokerSignaturePath = (brokerGroupId, userDocId, name) =>
	brokerStoragePath(
		brokerGroupId,
		P.signatures,
		userDocId,
		encodeSegment(name),
		'signature.html',
	);

/** userData/{userDocId}/{purpose}/… */
export const userStoragePath = (userDocId, purpose, ...segments) => {
	assertPurpose('userData', purpose);
	return joinPath('userData', userDocId, purpose, ...segments);
};

/** clientData/{clientDocId}/{purpose}/… */
export const clientStoragePath = (clientDocId, purpose, ...segments) => {
	assertPurpose('clientData', purpose);
	return joinPath('clientData', clientDocId, purpose, ...segments);
};

/** relationshipCompanyData/{relationshipCompanyId}/{purpose}/… */
export const relationshipCompanyStoragePath = (relationshipCompanyId, purpose, ...segments) => {
	assertPurpose('relationshipCompanyData', purpose);
	return joinPath('relationshipCompanyData', relationshipCompanyId, purpose, ...segments);
};

/**
 * clientData/{clientDocId}/deals/{dealDocId}, or the client-less deals/{dealDocId} when
 * the deal has no client yet.
 */
export const dealStorageRoot = ({ clientDocId, dealDocId }) =>
	clientDocId
		? joinPath('clientData', clientDocId, 'deals', dealDocId)
		: joinPath('deals', dealDocId);

/** {dealStorageRoot}/{purpose}/… */
export const dealStoragePath = (ids, purpose, ...segments) => {
	assertPurpose('deals', purpose);
	return `${dealStorageRoot(ids)}/${joinPath(purpose, ...segments)}`;
};

/** {root}/{templateDocId}/… for root 'unlayerEmailTemplates' or 'aiHtmlTemplates'. */
export const templateStoragePath = (root, templateDocId, ...segments) => {
	if (!TEMPLATE_ROOTS.includes(root)) {
		throw new Error(`Unknown template storage root: ${root}`);
	}
	return joinPath(root, templateDocId, ...segments);
};
